import { jsx as _jsx } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { ReportActionSource } from '@amzn/handshake-painter-typescript-client/v1';
import { ExternalLink } from 'src/common/externalLink';
import { RetailRoutes } from 'src/utils/links';
import { PreviewOrderErrorType, PreviewOrderStatus } from 'src/pages/previewOrderPage/utils';
/**
 * Resolves the alert to show for an order preview: an order-change notice on success, or the error message for
 * an unfixable, payment method or delivery address error. Returns null when there is nothing to show.
 *
 * Returns the alert rather than showing it so each surface decides where it renders: POP passes it to the page
 * level `showAlert`, while the 1-Click sheet renders it inline because a page alert would sit behind the sheet.
 */
export const usePreviewOrderAlert = ({ orderPreview, orderStatus, errorType, translationKey, bundle, redirectUrls, selectedPaymentMethodId, source = ReportActionSource.PreviewOrderPage, }) => {
    const offerDetailsChanged = !!(orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.offerDetailsChanged);
    const detailPageUrl = redirectUrls[RetailRoutes.DETAIL_PAGE];
    const paymentWalletUrl = redirectUrls[RetailRoutes.PAYMENT_SELECTION_WALLET];
    const addressListUrl = redirectUrls[RetailRoutes.ADDRESS_LIST];
    return useMemo(() => {
        // Each link in the message shares the same attributes
        const formattedLink = (chunk, url, id) => (_jsx(ExternalLink, { id: id, underline: true, href: url, source: source, children: chunk }, id));
        if (orderStatus === PreviewOrderStatus.SUCCESS && offerDetailsChanged) {
            return {
                type: 'neutral',
                title: bundle.getMessage('orderUpdateAlertMessage'),
                message: bundle.getMessage(translationKey),
            };
        }
        else if (orderStatus === PreviewOrderStatus.UNFIXABLE_ERROR) {
            return {
                type: 'error',
                message: bundle.formatMessage(translationKey, {
                    a: (chunk) => formattedLink(chunk, detailPageUrl, 'detail-page-link'),
                }),
            };
        }
        else if (errorType === PreviewOrderErrorType.PAYMENT_METHOD) {
            return {
                type: 'warning',
                message: bundle.formatMessage(translationKey, {
                    method: selectedPaymentMethodId ? 'selected' : 'other',
                    a: (chunk) => formattedLink(chunk, paymentWalletUrl, 'wallet-link'),
                }),
            };
        }
        else if (errorType === PreviewOrderErrorType.DELIVERY_ADDRESS) {
            return {
                type: 'warning',
                message: bundle.formatMessage(translationKey, {
                    a: (chunk) => formattedLink(chunk, addressListUrl, 'address-list-link'),
                }),
            };
        }
        return null;
    }, [
        bundle,
        translationKey,
        errorType,
        orderStatus,
        offerDetailsChanged,
        selectedPaymentMethodId,
        detailPageUrl,
        paymentWalletUrl,
        addressListUrl,
        source,
    ]);
};
